import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "./ConfirmDialog";
import { Input } from "./ui/input";
import {
  Search, RefreshCw, Lock, ClipboardList, PlusCircle, Pencil, Move, Trash2, RotateCcw, Undo2,
} from "lucide-react";

const ACTION_LABELS = {
  create: "Adăugare", update: "Editare", move: "Mutare",
  delete: "Ștergere", restore: "Restaurare",
};
const ENTITY_LABELS = {
  element: "Element", fence: "Gard", guard: "Punct pază",
  category: "Categorie", map: "Hartă", backup: "Backup",
};
const ACTION_STYLE = {
  create: { bg: "#DCFCE7", fg: "#166534", Icon: PlusCircle },
  update: { bg: "#DBEAFE", fg: "#1E3A8A", Icon: Pencil },
  move: { bg: "#FEF3C7", fg: "#92400E", Icon: Move },
  delete: { bg: "#FEE2E2", fg: "#991B1B", Icon: Trash2 },
  restore: { bg: "#EDE9FE", fg: "#5B21B6", Icon: RotateCcw },
};

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("ro-RO", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const changesText = (changes) =>
  (changes || []).map((c) => `${c.field}: ${fmtVal(c.from)} → ${fmtVal(c.to)}`).join(" · ");

export default function ActivityLog() {
  const { isSuperAdmin, loading, login, isAuthenticated } = useAuth();
  const confirm = useConfirm();
  const [logs, setLogs] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [search, setSearch] = useState("");
  const [person, setPerson] = useState("");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async () => {
    setFetching(true);
    try {
      const { data } = await api.get("/activity-log");
      setLogs(data || []);
    } catch { /* handled by interceptor */ }
    setFetching(false);
  };

  // count of later mutations on the same entity (to warn before undoing an old op)
  const laterCountFor = (row) => {
    if (!row.entity_id) return 0;
    return logs.filter(
      (l) => l.entity_id === row.entity_id &&
        l.id !== row.id &&
        ["create", "update", "move", "delete"].includes(l.action) &&
        (l.timestamp || "") > (row.timestamp || "")
    ).length;
  };

  const [undoing, setUndoing] = useState("");
  const doUndo = async (row) => {
    const later = laterCountFor(row);
    let msg = `Sigur anulezi această operație?\n\n${row.summary}`;
    if (later > 0) {
      msg = `ATENȚIE: după această operație au mai avut loc ${later} modificări ale aceluiași element. ` +
        `Anularea va readuce elementul la starea de ATUNCI și va suprascrie modificările ulterioare.\n\n` +
        `Sigur continui?\n\n${row.summary}`;
    }
    if (!(await confirm({ title: "Anulează operația", danger: true, confirmText: "Anulează operația", cancelText: "Renunță", description: msg }))) return;
    setUndoing(row.id);
    try {
      await api.post(`/activity-log/${row.id}/undo`);
      toast.success("Operație anulată. Reîncarcă pagina hărții pentru a vedea schimbarea.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anulare eșuată");
    }
    setUndoing("");
  };

  useEffect(() => {
    if (isSuperAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const people = useMemo(
    () => [...new Set(logs.map((l) => l.actor_name).filter(Boolean))].sort(),
    [logs]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((l) => {
      if (person && l.actor_name !== person) return false;
      if (action && l.action !== action) return false;
      const day = (l.timestamp || "").slice(0, 10);
      if (from && day < from) return false;
      if (to && day > to) return false;
      if (q) {
        const hay = [
          l.actor_name, l.actor_email, ACTION_LABELS[l.action], l.action,
          ENTITY_LABELS[l.entity_type], l.entity_type, l.entity_name, l.summary,
          changesText(l.changes), fmtDate(l.timestamp),
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [logs, search, person, action, from, to]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-zinc-500">Se încarcă…</div>;
  }

  if (!isAuthenticated || !isSuperAdmin) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50">
        <div className="text-center space-y-4 max-w-sm px-6">
          <Lock size={40} className="mx-auto text-zinc-400" />
          <h1 className="font-head text-xl font-bold text-zinc-900">Acces restricționat</h1>
          <p className="text-sm text-zinc-500">
            Jurnalul de activitate este disponibil doar administratorilor autentificați.
          </p>
          <button data-testid="log-login-btn" onClick={login}
            className="px-4 h-10 bg-[#5E8A1E] text-white text-sm font-medium hover:opacity-90">
            Autentificare
          </button>
        </div>
      </div>
    );
  }

  const selCls = "h-9 px-2 border border-[#A8C97D] rounded-none text-sm bg-white focus:border-[#5E8A1E] focus:outline-none";

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="activity-log-page">
      <header className="h-14 bg-[#5E8A1E] text-white flex items-center justify-between px-5 shrink-0">
        <span className="flex items-center gap-2 font-head font-bold tracking-wide">
          <ClipboardList size={18} strokeWidth={2} /> Jurnal de activitate
        </span>
        <button data-testid="log-refresh-btn" onClick={load}
          className="flex items-center gap-2 text-sm bg-white/15 hover:bg-white/25 px-3 h-8">
          <RefreshCw size={14} className={fetching ? "animate-spin" : ""} /> Reîmprospătează
        </button>
      </header>

      <div className="p-5 space-y-4 flex-1 overflow-hidden flex flex-col">
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="log-search" placeholder="Caută în toate câmpurile…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-7 h-9 rounded-none border-[#A8C97D] text-sm" />
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-zinc-500">Persoană</span>
            <select data-testid="log-filter-person" className={selCls} value={person}
              onChange={(e) => setPerson(e.target.value)}>
              <option value="">Toate persoanele</option>
              {people.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-zinc-500">Tip modificare</span>
            <select data-testid="log-filter-action" className={selCls} value={action}
              onChange={(e) => setAction(e.target.value)}>
              <option value="">Toate tipurile</option>
              {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-zinc-500">De la</span>
            <input data-testid="log-filter-from" type="date" className={selCls} value={from}
              onChange={(e) => setFrom(e.target.value)} />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-zinc-500">Până la</span>
            <input data-testid="log-filter-to" type="date" className={selCls} value={to}
              onChange={(e) => setTo(e.target.value)} />
          </label>

          <button data-testid="log-clear-filters" onClick={() => { setSearch(""); setPerson(""); setAction(""); setFrom(""); setTo(""); }}
            className="h-9 px-3 border border-[#A8C97D] text-sm text-zinc-600 hover:border-[#5E8A1E] bg-white">
            Resetează
          </button>
        </div>

        <div className="text-xs text-zinc-500 font-mono-data" data-testid="log-count">
          {filtered.length} din {logs.length} intrări
        </div>

        <div className="flex-1 overflow-auto border border-[#C3DBA0] bg-white">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-zinc-100 text-zinc-600">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold whitespace-nowrap">Data / ora</th>
                <th className="px-3 py-2 font-semibold whitespace-nowrap">Cine</th>
                <th className="px-3 py-2 font-semibold whitespace-nowrap">Acțiune</th>
                <th className="px-3 py-2 font-semibold whitespace-nowrap">Tip</th>
                <th className="px-3 py-2 font-semibold">Detalii</th>
                <th className="px-3 py-2 font-semibold whitespace-nowrap text-right">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-zinc-400">Nicio intrare</td></tr>
              ) : filtered.map((l) => {
                const st = ACTION_STYLE[l.action] || ACTION_STYLE.update;
                const Icon = st.Icon;
                const later = laterCountFor(l);
                return (
                  <tr key={l.id} data-testid={`log-row-${l.id}`} className="border-t border-zinc-100 hover:bg-zinc-50 align-top">
                    <td className="px-3 py-2 font-mono-data text-xs text-zinc-500 whitespace-nowrap">{fmtDate(l.timestamp)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="font-medium text-zinc-800">{l.actor_name}</div>
                      {l.actor_email && <div className="text-[11px] text-zinc-400">{l.actor_email}</div>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium"
                        style={{ background: st.bg, color: st.fg }}>
                        <Icon size={12} strokeWidth={2} /> {ACTION_LABELS[l.action] || l.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-zinc-600">{ENTITY_LABELS[l.entity_type] || l.entity_type}</td>
                    <td className="px-3 py-2 text-zinc-700">
                      <div>{l.summary}</div>
                      {l.changes && l.changes.length > 0 && (
                        <div className="text-[11px] text-zinc-400 font-mono-data mt-0.5">{changesText(l.changes)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      {l.undone ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-zinc-400">
                          <Undo2 size={12} /> Anulat{l.undone_by ? ` de ${l.undone_by}` : ""}
                        </span>
                      ) : l.undoable ? (
                        <button data-testid={`log-undo-${l.id}`} onClick={() => doUndo(l)} disabled={undoing === l.id}
                          title={later > 0 ? `Atenție: ${later} modificări ulterioare` : "Anulează această operație"}
                          className={`inline-flex items-center gap-1 px-2 h-7 text-xs border transition-colors disabled:opacity-50 ${
                            later > 0
                              ? "border-[#F59E0B] text-[#B45309] hover:bg-[#F59E0B] hover:text-white"
                              : "border-[#A8C97D] text-zinc-700 hover:bg-[#5E8A1E] hover:text-white hover:border-[#5E8A1E]"
                          }`}>
                          <Undo2 size={13} strokeWidth={2} /> Undo{later > 0 ? ` (${later}!)` : ""}
                        </button>
                      ) : (
                        <span className="text-[11px] text-zinc-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
